import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../config/api';
import { fetchDynamicPrice, fetchLegsPrice } from '../slices/materialsSlice';
import {
	calculateRoofPitchAdjustment,
	calculateSubTotal,
} from '../utils/PricingEngine';

const priceCache = new Map();

export const usePricing = ({
	dispatch,
	token,
	selectedStyle,
	cSWidth,
	cSLength,
	cSHeight,
	filterCategoryId,
	selectedRoofStyle,
	selectedRoofPitch,
	leftLean,
	lLWidth,
	lLLength,
	lLHeight,
	rightLean,
	rLWidth,
	rLLength,
	rLHeight,
	frontLean,
	fLWidth,
	fLLength,
	fLHeight,
	backLean,
	bLWidth,
	bLLength,
	bLHeight,
	sideEndData,
	totalSideWallPrice,
	doorWindowSelections,
	sidewallCosts,
	totalDiscount,
	centerStorage,
	storageLocation,
	dephCentralStorage,
	dephLeftStorage,
	dephRightStorage,
	dephFrontStorage,
	dephBackStorage,
    formAdditionalOptionsData,
}) => {
	const [totalCenterGauge, setTotalCenterGauge] = useState(0);
	const [totalCenterLegPrice, setTotalCenterLegPrice] = useState(0);
	const [leftLeanPrice, setLeftLeanPrice] = useState(0);
	const [totalLeftLegPrice, setTotalLeftLegPrice] = useState(0);
	const [rightLeanPrice, setRightLeanPrice] = useState(0);
	const [totalRightLegPrice, setTotalRightLegPrice] = useState(0);
	const [frontLeanPrice, setFrontLeanPrice] = useState(0);
	const [totalFrontLegPrice, setTotalFrontLegPrice] = useState(0);
	const [backLeanPrice, setBackLeanPrice] = useState(0);
	const [totalBackLegPrice, setTotalBackLegPrice] = useState(0);
    const [insulationPriceBreakdown, setInsulationPriceBreakdown] = useState({});
	const [totalRoofStylePrice, setTotalRoofStylePrice] = useState(0);
	const [totalRoofPitchPrice, setTotalRoofPitchPrice] = useState(0);
	const [leftSideRoofPitchPrice, setLeftSideRoofPitchPrice] = useState(0);
	const [rightSideRoofPitchPrice, setRightSideRoofPitchPrice] = useState(0);
	const [frontRoofPitchPrice, setFrontSideRoofPitchPrice] = useState(0);
	const [backRoofPitchPrice, setBackSideRoofPitchPrice] = useState(0);
	// Storage prices
	const [centerStoragePrice, setCenterStoragePrice] = useState(0);
	const [leftleanstoragePrice, setLeftLeanStoragePrice] = useState(0);
	const [rightStoragePrice, setRightStoragePrice] = useState(0);
	const [frontStoragePrice, setFrontStoragePrice] = useState(0);
	const [backStoragePrice, setBackStoragePrice] = useState(0);

	/**
	 * Recursively fetches dynamic price for a given length.
	 */
	const getDynamicPrice = useCallback(async (params) => {
		const { width, length, height, categoryId, token } = params;
		const numericLength = Number(length);
		const cacheKey = `${width || 'w'}-${numericLength}-${
			height || 'h'
		}-${categoryId}`;

		if (priceCache.has(cacheKey)) return priceCache.get(cacheKey);

		const fetchPriceForLength = async (len) => {
			const apiParams = {
				pageSize: 1,
				page: 1,
				sortOrder: 'DESC',
				categoryId,
				length: len,
			};
			if (width) apiParams.width = width;
			if (height) apiParams.height = height;

			try {
				const response = await api.get('/Materials', {
					params: apiParams,
					headers: { Authorization: `Bearer ${token}` },
				});
				return response.data.items?.[0]?.defaultPrice || 0;
			} catch (err) {
				console.error('Error fetching price for params:', apiParams, err);
				return 0;
			}
		};

		const price = await fetchPriceForLength(numericLength);
		priceCache.set(cacheKey, price);
		return price;
	}, []);

	// Center Storage Price
	useEffect(() => {
		if (
			centerStorage === 'none' ||
			dephCentralStorage === 'none' ||
			!cSWidth ||
			!filterCategoryId
		) {
			setCenterStoragePrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchDynamicPrice({
					width: filterCategoryId === 34 ? undefined : cSWidth,
					height: filterCategoryId === 34 ? cSHeight : undefined,
					length: dephCentralStorage,
					categoryId: filterCategoryId,
				})
			);
			if (fetchDynamicPrice.fulfilled.match(result))
				setCenterStoragePrice(result.payload || 0);
		};
		calculate();
	}, [
		centerStorage,
		dephCentralStorage,
		cSWidth,
		cSHeight,
		filterCategoryId,
		dispatch,
	]);

	// Left Lean Storage Price
	useEffect(() => {
		if (
			leftLean === 'none' ||
			dephLeftStorage === 'none' ||
			!lLWidth ||
			!filterCategoryId
		) {
			setLeftLeanStoragePrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchDynamicPrice({
					width: filterCategoryId === 34 ? undefined : lLWidth,
					height: filterCategoryId === 34 ? lLHeight : undefined,
					length: dephLeftStorage,
					categoryId: filterCategoryId,
				})
			);
			if (fetchDynamicPrice.fulfilled.match(result))
				setLeftLeanStoragePrice(result.payload || 0);
		};
		calculate();
	}, [
		leftLean,
		dephLeftStorage,
		lLWidth,
		lLHeight,
		filterCategoryId,
		dispatch,
	]);

	// Right Lean Storage Price
	useEffect(() => {
		if (
			rightLean === 'none' ||
			dephRightStorage === 'none' ||
			!rLWidth ||
			!filterCategoryId
		) {
			setRightStoragePrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchDynamicPrice({
					width: filterCategoryId === 34 ? undefined : rLWidth,
					height: filterCategoryId === 34 ? rLHeight : undefined,
					length: dephRightStorage,
					categoryId: filterCategoryId,
				})
			);
			if (fetchDynamicPrice.fulfilled.match(result))
				setRightStoragePrice(result.payload || 0);
		};
		calculate();
	}, [
		rightLean,
		dephRightStorage,
		rLWidth,
		rLHeight,
		filterCategoryId,
		dispatch,
	]);

	// Front Lean Storage Price
	useEffect(() => {
		if (
			frontLean === 'none' ||
			dephFrontStorage === 'none' ||
			!fLWidth ||
			!filterCategoryId
		) {
			setFrontStoragePrice(0);
			return;
		}
		const calculate = async () => {
			const price = await getDynamicPrice({
				width: filterCategoryId === 34 ? undefined : fLWidth,
				height: filterCategoryId === 34 ? fLHeight : undefined,
				length: dephFrontStorage,
				categoryId: filterCategoryId,
				token,
			});
			setFrontStoragePrice(price || 0);
		};
		calculate();
	}, [
		frontLean,
		dephFrontStorage,
		fLWidth,
		fLHeight,
		filterCategoryId,
		token,
		getDynamicPrice,
	]);

	// Back Lean Storage Price
	useEffect(() => {
		if (
			backLean === 'none' ||
			dephBackStorage === 'none' ||
			!bLWidth ||
			!filterCategoryId
		) {
			setBackStoragePrice(0);
			return;
		}
		const calculate = async () => {
			const price = await getDynamicPrice({
				width: filterCategoryId === 34 ? undefined : bLWidth,
				height: filterCategoryId === 34 ? bLHeight : undefined,
				length: dephBackStorage,
				categoryId: filterCategoryId,
				token,
			});
			setBackStoragePrice(price || 0);
		};
		calculate();
	}, [
		backLean,
		dephBackStorage,
		bLWidth,
		bLHeight,
		filterCategoryId,
		token,
		getDynamicPrice,
	]);

	// Categorize legs
	const legCategory = selectedStyle === 'wsd' ? 20 : 5;

	// Effects for each price component...
	// Center Gauge
	useEffect(() => {
		if (!cSWidth || !cSLength || !filterCategoryId) {
			setTotalCenterGauge(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchDynamicPrice({
					width: filterCategoryId === 34 ? undefined : Number(cSWidth),
					height: filterCategoryId === 34 ? Number(cSHeight) : undefined,
					length: Number(cSLength),
					categoryId: filterCategoryId,
				})
			);
			if (fetchDynamicPrice.fulfilled.match(result))
				setTotalCenterGauge(result.payload || 0);
		};
		calculate();
	}, [cSWidth, cSLength, cSHeight, filterCategoryId, dispatch]);

	// Leg Prices (Center)
	useEffect(() => {
		if (!cSHeight || !cSLength) {
			setTotalCenterLegPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchLegsPrice({
					height: Number(cSHeight),
					length: Number(cSLength),
					width: Number(cSWidth),
					categoryId: legCategory,
				})
			);
			if (fetchLegsPrice.fulfilled.match(result)) {
				const firstItemPrice = result.payload.items?.[0]?.defaultPrice || 0;
				setTotalCenterLegPrice(firstItemPrice);
			}
		};
		calculate();
	}, [cSHeight, cSLength, cSWidth, legCategory, dispatch]);

	// Lean-to Gauge Prices (Left, Right, Front, Back)
	// Left
	useEffect(() => {
		if (leftLean === 'none' || !lLWidth || !lLLength || !filterCategoryId) {
			setLeftLeanPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchDynamicPrice({
					width: filterCategoryId === 34 ? undefined : lLWidth,
					height: filterCategoryId === 34 ? lLHeight : undefined,
					length: lLLength,
					categoryId: filterCategoryId,
				})
			);
			if (fetchDynamicPrice.fulfilled.match(result))
				setLeftLeanPrice(result.payload || 0);
		};
		calculate();
	}, [leftLean, lLWidth, lLLength, lLHeight, filterCategoryId, dispatch]);

	// Right
	useEffect(() => {
		if (rightLean === 'none' || !rLWidth || !rLLength || !filterCategoryId) {
			setRightLeanPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchDynamicPrice({
					width: filterCategoryId === 34 ? undefined : rLWidth,
					height: filterCategoryId === 34 ? rLHeight : undefined,
					length: rLLength,
					categoryId: filterCategoryId,
				})
			);
			if (fetchDynamicPrice.fulfilled.match(result))
				setRightLeanPrice(result.payload || 0);
		};
		calculate();
	}, [rightLean, rLWidth, rLLength, rLHeight, filterCategoryId, dispatch]);

	// Front
	useEffect(() => {
		if (frontLean === 'none' || !fLWidth || !fLLength || !filterCategoryId) {
			setFrontLeanPrice(0);
			return;
		}
		const calculate = async () => {
			const price = await getDynamicPrice({
				width: filterCategoryId === 34 ? undefined : fLWidth,
				height: filterCategoryId === 34 ? fLHeight : undefined,
				length: fLLength,
				categoryId: filterCategoryId,
				token,
			});
			setFrontLeanPrice(price || 0);
		};
		calculate();
	}, [
		frontLean,
		fLWidth,
		fLLength,
		fLHeight,
		filterCategoryId,
		token,
		getDynamicPrice,
	]);

	// Back
	useEffect(() => {
		if (backLean === 'none' || !bLWidth || !bLLength || !filterCategoryId) {
			setBackLeanPrice(0);
			return;
		}
		const calculate = async () => {
			const price = await getDynamicPrice({
				width: filterCategoryId === 34 ? undefined : bLWidth,
				height: filterCategoryId === 34 ? bLHeight : undefined,
				length: bLLength,
				categoryId: filterCategoryId,
				token,
			});
			setBackLeanPrice(price || 0);
		};
		calculate();
	}, [
		backLean,
		bLWidth,
		bLLength,
		bLHeight,
		filterCategoryId,
		token,
		getDynamicPrice,
	]);

	// Leg Prices for Lean-tos (Left, Right, Front, Back)
	// Left Lean Legs
	useEffect(() => {
		if (leftLean === 'none' || !lLHeight || !lLLength) {
			setTotalLeftLegPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchLegsPrice({
					height: lLHeight,
					length: lLLength,
					width: lLWidth,
					categoryId: legCategory,
				})
			);
			if (fetchLegsPrice.fulfilled.match(result)) {
				setTotalLeftLegPrice(result.payload.items?.[0]?.defaultPrice || 0);
			}
		};
		calculate();
	}, [leftLean, lLHeight, lLLength, lLWidth, legCategory, dispatch]);

	// Right Lean Legs
	useEffect(() => {
		if (rightLean === 'none' || !rLHeight || !rLLength) {
			setTotalRightLegPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchLegsPrice({
					height: rLHeight,
					length: rLLength,
					width: rLWidth,
					categoryId: legCategory,
				})
			);
			if (fetchLegsPrice.fulfilled.match(result)) {
				setTotalRightLegPrice(result.payload.items?.[0]?.defaultPrice || 0);
			}
		};
		calculate();
	}, [rightLean, rLHeight, rLLength, rLWidth, legCategory, dispatch]);

	// Front Lean Legs
	useEffect(() => {
		if (frontLean === 'none' || !fLHeight || !fLLength) {
			setTotalFrontLegPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchLegsPrice({
					height: fLHeight,
					length: fLLength,
					width: fLWidth,
					categoryId: legCategory,
				})
			);
			if (fetchLegsPrice.fulfilled.match(result)) {
				setTotalFrontLegPrice(result.payload.items?.[0]?.defaultPrice || 0);
			}
		};
		calculate();
	}, [frontLean, fLHeight, fLLength, fLWidth, legCategory, dispatch]);

	// Back Lean Legs
	useEffect(() => {
		if (backLean === 'none' || !bLHeight || !bLLength) {
			setTotalBackLegPrice(0);
			return;
		}
		const calculate = async () => {
			const result = await dispatch(
				fetchLegsPrice({
					height: bLHeight,
					length: bLLength,
					width: bLWidth,
					categoryId: legCategory,
				})
			);
			if (fetchLegsPrice.fulfilled.match(result)) {
				setTotalBackLegPrice(result.payload.items?.[0]?.defaultPrice || 0);
			}
		};
		calculate();
	}, [backLean, bLHeight, bLLength, bLWidth, legCategory, dispatch]);

	// Roof style price
	useEffect(() => {
		const fetchPrice = async () => {
			if (!selectedRoofStyle || selectedRoofStyle === 'standard') {
				setTotalRoofStylePrice(0);
				return;
			}

			// Category ID 46 for Horizontal, 47 for Vertical
			// Category ID 3 for Horizontal, 4 for Vertical changed to match 3design portal
			const catId = selectedRoofStyle === 'horizontal' ? 3 : 4;

			// Calculate total roof style price across all building sections
			let totalPrice = 0;

			// Helper to get adjusted roof price for a section
			const getSectionRoofPrice = async (width, length) => {
				if (!width || !length) return 0;
				try {
					const adjustedLength = Number(length) + 1; // +1ft Rule
					const res = await api.get('/Materials', {
						params: {
							pageSize: 1,
							page: 1,
							sortOrder: 'DESC',
							categoryId: catId,
							length: adjustedLength,
							width: width,
						},
						headers: { Authorization: `Bearer ${token}` },
					});
					return (res.data.items || [])?.[0]?.defaultPrice || 0;
				} catch (err) {
					console.error('Error fetching section roof style price:', err);
					return 0;
				}
			};

			// 1. Center Section
			const centerPrice = await getSectionRoofPrice(cSWidth, cSLength);
			totalPrice += centerPrice;

			// 2. Lean-tos (only if they are enabled)
			if (leftLean !== 'none')
				totalPrice += await getSectionRoofPrice(lLWidth, lLLength);
			if (rightLean !== 'none')
				totalPrice += await getSectionRoofPrice(rLWidth, rLLength);
			if (frontLean !== 'none')
				totalPrice += await getSectionRoofPrice(fLWidth, fLLength);
			if (backLean !== 'none')
				totalPrice += await getSectionRoofPrice(bLWidth, bLLength);

			setTotalRoofStylePrice(totalPrice);
		};
		fetchPrice();
	}, [
		selectedRoofStyle,
		token,
		cSWidth,
		cSLength,
		leftLean,
		lLWidth,
		lLLength,
		rightLean,
		rLWidth,
		rLLength,
		frontLean,
		fLWidth,
		fLLength,
		backLean,
		bLWidth,
		bLLength,
	]);

	// Roof pitch adjustments
	useEffect(() => {
		setTotalRoofPitchPrice(
			calculateRoofPitchAdjustment(totalCenterGauge, selectedRoofPitch)
		);
	}, [selectedRoofPitch, totalCenterGauge]);

	useEffect(() => {
		const pitch =
			sideEndData?.configuration?.rightLeanToSide?.rightLeanRoofPitch;
		setRightSideRoofPitchPrice(
			calculateRoofPitchAdjustment(rightLeanPrice, pitch)
		);
	}, [sideEndData, rightLeanPrice]);

	useEffect(() => {
		const pitch = sideEndData?.configuration?.leftLeanToSide?.leftLeanRoofPitch;
		setLeftSideRoofPitchPrice(
			calculateRoofPitchAdjustment(leftLeanPrice, pitch)
		);
	}, [sideEndData, leftLeanPrice]);

	useEffect(() => {
		const pitch =
			sideEndData?.configuration?.frontLeanToSide?.frontLeanRoofPitch;
		setFrontSideRoofPitchPrice(
			calculateRoofPitchAdjustment(frontLeanPrice, pitch)
		);
	}, [sideEndData, frontLeanPrice]);

	useEffect(() => {
		const pitch = sideEndData?.configuration?.backLeanToSide?.backLeanRoofPitch;
		setBackSideRoofPitchPrice(
			calculateRoofPitchAdjustment(backLeanPrice, pitch)
		);
	}, [sideEndData, backLeanPrice]);

	// Insulation Price
	const [insulationUnitPrice, setInsulationUnitPrice] = useState(0);
	const [totalInsulationPrice, setTotalInsulationPrice] = useState(0);

	// Fetch Insulation Unit Price (Category 26)
	useEffect(() => {
		const fetchInsulationPrice = async () => {
			try {
				const response = await api.get('/Materials', {
					params: {
						pageSize: 1,
						page: 1,
						categoryId: 26,
                        width: 1,
                        length: 1,
                        height: 1,
					},
					headers: { Authorization: `Bearer ${token}` },
				});
				// Assuming standard insulation item is the first one or we need a specific one.
                // Using the first item's default price as per instruction "call it from categoryId 26"
				if (response.data.items && response.data.items.length > 0) {
                    console.log('Fetched Insulation Unit Price:', response.data.items[0].defaultPrice);
					setInsulationUnitPrice(response.data.items[0].defaultPrice);
				} else {
                    console.warn('No items found for Insulation Category 26. Using default fallback $2.25');
                    setInsulationUnitPrice(2.25);
                }
			} catch (err) {
				console.error('Error fetching insulation price:', err);
                 // Fallback on error too? Maybe safer for user testing right now.
                 console.warn('Using default fallback $2.25 due to error');
                 setInsulationUnitPrice(2.25);
			}
		};
		fetchInsulationPrice();
	}, [token]);

	// Calculate Total Insulation Price
	useEffect(() => {
        // Validation: Ensure unit price is available
		if (!insulationUnitPrice) {
            console.log('Insulation Unit Price is 0 or not loaded');
			setTotalInsulationPrice(0);
            setInsulationPriceBreakdown({});
			return;
		}

		let totalPrice = 0;
        // Initialize breakdown with 0s to ensure we don't return undefined
        let breakdown = {
            roofInsulationCS: 0, backInsulationCS: 0, frontInsulationCS: 0, leftInsulationCS: 0, rightInsulationCS: 0,
            roofInsulationLS: 0, backInsulationLS: 0, frontInsulationLS: 0, leftInsulationLS: 0,
            roofInsulationRS: 0, backInsulationRS: 0, frontInsulationRS: 0, rightInsulationRS: 0,
            roofInsulationFS: 0
        };

		const {
			roofInsulationCS, backInsulationCS, frontInsulationCS, leftInsulationCS, rightInsulationCS,
			roofInsulationLS, backInsulationLS, leftInsulationLS, frontInsulationLS,
			roofInsulationRS, backInsulationRS, rightInsulationRS, frontInsulationRS,
			roofInsulationFS
		} = formAdditionalOptionsData || {};

        // Helper for dimensions (parse to float to be safe)
        const cW = parseFloat(cSWidth) || 0;
        const cL = parseFloat(cSLength) || 0;
        const cH = parseFloat(cSHeight) || 0;

        const lW = parseFloat(lLWidth) || 0;
        const lL = parseFloat(lLLength) || 0;
        const lH = parseFloat(lLHeight) || 0;

        const rW = parseFloat(rLWidth) || 0;
        const rL = parseFloat(rLLength) || 0;
        const rH = parseFloat(rLHeight) || 0;

        const fW = parseFloat(fLWidth) || 0;
        const fL = parseFloat(fLLength) || 0;
        // const fH = parseFloat(fLHeight) || 0; 

        console.log('Calculating Insulation Price. Unit Price:', insulationUnitPrice);
        console.log('Dimensions CS:', cSWidth, cSLength, cSHeight);
        console.log('Parsed CS:', cW, cL, cH);
        
        // --- Center Section ---
		if (roofInsulationCS) {
			const p = (cW + 3) * cL * insulationUnitPrice;
            console.log('Calc Roof CS: ', p, `(${cW} + 3) * ${cL} * ${insulationUnitPrice}`);
            totalPrice += p;
            breakdown.roofInsulationCS = p;
		}
		if (backInsulationCS) {
            if (sideEndData?.configuration?.centerSectionSide?.backGableEnd === 'fullyEnclosed') {
                const p = (cH + 3) * cW * insulationUnitPrice;
                totalPrice += p;
                breakdown.backInsulationCS = p;
            }
		}
		if (frontInsulationCS) {
            if (sideEndData?.configuration?.centerSectionSide?.frontGableEnd === 'fullyEnclosed') {
                const p = (cH + 3) * cW * insulationUnitPrice;
                totalPrice += p;
                breakdown.frontInsulationCS = p;
            }
		}
		if (leftInsulationCS) {
            if (sideEndData?.configuration?.centerSectionSide?.leftSidewall === 'fullyEnclosed') {
                const p = (cH + 2) * cL * insulationUnitPrice;
                totalPrice += p;
                breakdown.leftInsulationCS = p;
            }
		}
		if (rightInsulationCS) {
            if (sideEndData?.configuration?.centerSectionSide?.rightSidewall === 'fullyEnclosed') {
                const p = (cH + 2) * cL * insulationUnitPrice;
                totalPrice += p;
                breakdown.rightInsulationCS = p;
            }
		}

        // --- Left Lean-to ---
		if (leftLean !== 'none') {
            if (roofInsulationLS) {
                const p = (lW + 3) * lL * insulationUnitPrice;
                totalPrice += p;
                breakdown.roofInsulationLS = p;
            }
            if (backInsulationLS) {
                if (sideEndData?.configuration?.leftLeanToSide?.backGableEnd === 'fullyEnclosed') {
                    const p = (lH + 3) * lW * insulationUnitPrice;
                    totalPrice += p;
                    breakdown.backInsulationLS = p;
                }
            }
            if (frontInsulationLS) {
                if (sideEndData?.configuration?.leftLeanToSide?.frontGableEnd === 'fullyEnclosed') {
                    const p = (lH + 3) * lW * insulationUnitPrice;
                    totalPrice += p;
                    breakdown.frontInsulationLS = p;
                }
            }
            if (leftInsulationLS) {
                if (sideEndData?.configuration?.leftLeanToSide?.leftSidewall === 'fullyEnclosed') {
                    const p = (lH + 2) * lL * insulationUnitPrice;
                    totalPrice += p;
                    breakdown.leftInsulationLS = p;
                }
            }
        }

        // --- Right Lean-to ---
        if (rightLean !== 'none') {
            if (roofInsulationRS) {
                const p = (rW + 3) * rL * insulationUnitPrice;
                totalPrice += p;
                breakdown.roofInsulationRS = p;
            }
            if (backInsulationRS) {
                if (sideEndData?.configuration?.rightLeanToSide?.backGableEnd === 'fullyEnclosed') {
                    const p = (rH + 3) * rW * insulationUnitPrice;
                    totalPrice += p;
                    breakdown.backInsulationRS = p;
                }
            }
            if (frontInsulationRS) {
                if (sideEndData?.configuration?.rightLeanToSide?.frontGableEnd === 'fullyEnclosed') {
                    const p = (rH + 3) * rW * insulationUnitPrice;
                    totalPrice += p;
                    breakdown.frontInsulationRS = p;
                }
            }
            if (rightInsulationRS) {
                if (sideEndData?.configuration?.rightLeanToSide?.rightSidewall === 'fullyEnclosed') {
                    const p = (rH + 2) * rL * insulationUnitPrice;
                    totalPrice += p;
                    breakdown.rightInsulationRS = p;
                }
            }
        }

        // --- Front Lean-to ---
        if (frontLean !== 'none') {
             if (roofInsulationFS) {
                const p = (fW + 3) * fL * insulationUnitPrice;
                totalPrice += p;
                breakdown.roofInsulationFS = p;
            }
        }
        
        console.log('Total Insulation Price Calculated:', totalPrice);
		setTotalInsulationPrice(totalPrice);
        setInsulationPriceBreakdown(breakdown);
	}, [
		insulationUnitPrice,
		formAdditionalOptionsData,
		cSWidth, cSLength, cSHeight,
		leftLean, lLWidth, lLLength, lLHeight,
		rightLean, rLWidth, rLLength, rLHeight,
		frontLean, fLWidth, fLLength, fLHeight,
        sideEndData // Added sideEndData dependency
	]);

	// Aggregated prices from selections

	const totalSidewallCost = useMemo(() => {
		if (!sidewallCosts) return 0;
		return Object.values(sidewallCosts).reduce((sectionAcc, section) => {
			const thisSectionTotal = Object.values(section).reduce(
				(sum, price) => sum + (price || 0),
				0
			);
			return sectionAcc + thisSectionTotal;
		}, 0);
	}, [sidewallCosts]);

	// Storage panels — sides and ends of each storage room
	// Dividing wall is always priced; back/left/right only when those sides are open in the main structure
	const [storagePanelsTotal, setStoragePanelsTotal] = useState(0);
	const [storagePanelsBreakdown, setStoragePanelsBreakdown] = useState({});

	useEffect(() => {
		const gableOrientation = formAdditionalOptionsData?.gableEndSidingPanels;
		const sideOrientation   = formAdditionalOptionsData?.sidewall;
		if (!gableOrientation || !sideOrientation) {
			setStoragePanelsTotal(0);
			setStoragePanelsBreakdown({});
			return;
		}

		const gableCatId = gableOrientation === 'vertical' ? 9 : 8;
		const sideCatId  = sideOrientation  === 'vertical' ? 6 : 35;
		const roundUp5   = (len) => { const v = Number(len); return v % 5 === 0 ? v : Math.ceil(v / 5) * 5; };
		const isOpen     = (val) => !val || val === '0' || val === 'open' || val === '';

		const fetchGable = async (width, height) => {
			if (!width || !height) return 0;
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'DESC', categoryId: gableCatId, width: Number(width), height: Number(height) },
					headers: { Authorization: `Bearer ${token}` },
				});
				return res.data.items?.[0]?.defaultPrice || 0;
			} catch { return 0; }
		};

		const fetchSide = async (length, height) => {
			if (!length || !height) return 0;
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'DESC', categoryId: sideCatId, length: roundUp5(length), height: Number(height) },
					headers: { Authorization: `Bearer ${token}` },
				});
				return res.data.items?.[0]?.defaultPrice || 0;
			} catch { return 0; }
		};

		// Returns individual wall costs for one storage section
		const calcStorageBreakdown = async (config, structWidth, structHeight, depth) => {
			if (!depth || depth === 'none' || depth === '' || !structWidth || !structHeight) {
				return { dividingWall: 0, backWall: 0, leftSide: 0, rightSide: 0 };
			}
			const dividingWall = await fetchGable(structWidth, structHeight);
			const backWall  = isOpen(config?.backGableEnd) ? await fetchGable(structWidth, structHeight) : 0;
			const leftSide  = isOpen(config?.leftSidewall)  ? await fetchSide(depth, structHeight) : 0;
			const rightSide = isOpen(config?.rightSidewall) ? await fetchSide(depth, structHeight) : 0;
			return { dividingWall, backWall, leftSide, rightSide };
		};

		const calculate = async () => {
			const breakdown = {};
			let total = 0;

			if (centerStorage && centerStorage !== 'none') {
				breakdown.centerStorage = await calcStorageBreakdown(sideEndData?.configuration?.centerSectionSide, cSWidth, cSHeight, dephCentralStorage);
			}
			if (leftLean === 'leanToWithStorage') {
				breakdown.leftStorage = await calcStorageBreakdown(sideEndData?.configuration?.leftLeanToSide, lLWidth, lLHeight, dephLeftStorage);
			}
			if (rightLean === 'leanToWithStorage') {
				breakdown.rightStorage = await calcStorageBreakdown(sideEndData?.configuration?.rightLeanToSide, rLWidth, rLHeight, dephRightStorage);
			}
			if (frontLean === 'leanToWithStorage') {
				breakdown.frontStorage = await calcStorageBreakdown(sideEndData?.configuration?.frontLeanToSide, fLWidth, fLHeight, dephFrontStorage);
			}
			if (backLean === 'leanToWithStorage') {
				breakdown.backStorage = await calcStorageBreakdown(sideEndData?.configuration?.backLeanToSide, bLWidth, bLHeight, dephBackStorage);
			}

			for (const section of Object.values(breakdown)) {
				total += section.dividingWall + section.backWall + section.leftSide + section.rightSide;
			}

			setStoragePanelsBreakdown(breakdown);
			setStoragePanelsTotal(total);
		};

		calculate();
	}, [
		token, sideEndData,
		formAdditionalOptionsData?.sidewall, formAdditionalOptionsData?.gableEndSidingPanels,
		centerStorage, dephCentralStorage, cSWidth, cSHeight,
		leftLean, dephLeftStorage, lLWidth, lLHeight,
		rightLean, dephRightStorage, rLWidth, rLHeight,
		frontLean, dephFrontStorage, fLWidth, fLHeight,
		backLean, dephBackStorage, bLWidth, bLHeight,
	]);

	// Foam Closure (cat 58) — unit price × L per active section, only when sidewall AND roof are both vertical
	const [foamUnitPrice, setFoamUnitPrice] = useState(0);
	useEffect(() => {
		const fetch = async () => {
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'ASC', categoryId: 58 },
					headers: { Authorization: `Bearer ${token}` },
				});
				setFoamUnitPrice(res.data.items?.[0]?.defaultPrice || 0);
			} catch (err) {
				console.error('Error fetching foam closure price:', err);
			}
		};
		fetch();
	}, [token]);

	const foamClosureTotal = useMemo(() => {
		if (
			!formAdditionalOptionsData?.foamClouser ||
			formAdditionalOptionsData?.sidewall !== 'vertical' ||
			selectedRoofStyle !== 'vertical'
		) return 0;

		let total = foamUnitPrice * (parseFloat(cSLength) || 0);
		if (leftLean !== 'none') total += foamUnitPrice * (parseFloat(lLLength) || 0);
		if (rightLean !== 'none') total += foamUnitPrice * (parseFloat(rLLength) || 0);
		if (frontLean !== 'none') total += foamUnitPrice * (parseFloat(fLLength) || 0);
		if (backLean !== 'none') total += foamUnitPrice * (parseFloat(bLLength) || 0);
		return total;
	}, [
		foamUnitPrice,
		formAdditionalOptionsData?.foamClouser,
		formAdditionalOptionsData?.sidewall,
		selectedRoofStyle,
		cSLength,
		leftLean, lLLength,
		rightLean, rLLength,
		frontLean, fLLength,
		backLean, bLLength,
	]);

	// Texas Cut (cat 57) — unit price × number of qualifying items with texasCut checked
	const [texasCutUnitPrice, setTexasCutUnitPrice] = useState(0);
	useEffect(() => {
		const fetch = async () => {
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'ASC', categoryId: 57 },
					headers: { Authorization: `Bearer ${token}` },
				});
				setTexasCutUnitPrice(res.data.items?.[0]?.defaultPrice || 0);
			} catch (err) {
				console.error('Error fetching Texas Cut price:', err);
			}
		};
		fetch();
	}, [token]);

	const texasCutTotal = useMemo(() => {
		const checkedCount = (doorWindowSelections || []).filter(
			(s) => s.metadata?.texasCut === true &&
				['frameouts', 'rollupDoor', 'garageDoor'].includes(s.category)
		).length;
		return texasCutUnitPrice * checkedCount;
	}, [texasCutUnitPrice, doorWindowSelections]);

	// Trusses (cat 48: 12–25ft wide, cat 49: 26–30ft wide) — DB lookup by (W, H) × quantity
	const getTrussCatId = (width) => {
		const w = parseFloat(width) || 0;
		if (w >= 12 && w <= 25) return 48;
		if (w > 25 && w <= 30) return 49;
		return null;
	};

	const [trussesPrices, setTrussesPrices] = useState({
		CS: 0, LS: 0, RS: 0, FS: 0, BS: 0,
	});

	useEffect(() => {
		const fetchTrussPrice = async (width, height, key) => {
			const catId = getTrussCatId(width);
			if (!catId || !width || !height) return 0;
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'ASC', categoryId: catId, width: Number(width), height: Number(height) },
					headers: { Authorization: `Bearer ${token}` },
				});
				return res.data.items?.[0]?.defaultPrice || 0;
			} catch (err) {
				console.error(`Error fetching truss price for ${key}:`, err);
				return 0;
			}
		};

		const calculate = async () => {
			const [CS, LS, RS, FS, BS] = await Promise.all([
				fetchTrussPrice(cSWidth, cSHeight, 'CS'),
				leftLean !== 'none' ? fetchTrussPrice(lLWidth, lLHeight, 'LS') : Promise.resolve(0),
				rightLean !== 'none' ? fetchTrussPrice(rLWidth, rLHeight, 'RS') : Promise.resolve(0),
				frontLean !== 'none' ? fetchTrussPrice(fLWidth, fLHeight, 'FS') : Promise.resolve(0),
				backLean !== 'none' ? fetchTrussPrice(bLWidth, bLHeight, 'BS') : Promise.resolve(0),
			]);
			setTrussesPrices({ CS, LS, RS, FS, BS });
		};

		calculate();
	}, [
		token,
		cSWidth, cSHeight,
		leftLean, lLWidth, lLHeight,
		rightLean, rLWidth, rLHeight,
		frontLean, fLWidth, fLHeight,
		backLean, bLWidth, bLHeight,
	]);

	const trussesTotalPrice = useMemo(() => {
		const qty = (key) => parseInt(formAdditionalOptionsData?.[`trusses${key}`], 10) || 0;
		return (
			trussesPrices.CS * qty('CS') +
			trussesPrices.LS * qty('LS') +
			trussesPrices.RS * qty('RS') +
			trussesPrices.FS * qty('FS') +
			trussesPrices.BS * qty('BS')
		);
	}, [trussesPrices, formAdditionalOptionsData]);

	// Anchors (cat 54) — unit price × quantity
	const [anchorUnitPrice, setAnchorUnitPrice] = useState(0);
	useEffect(() => {
		const fetchAnchorPrice = async () => {
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'ASC', categoryId: 54 },
					headers: { Authorization: `Bearer ${token}` },
				});
				setAnchorUnitPrice(res.data.items?.[0]?.defaultPrice || 0);
			} catch (err) {
				console.error('Error fetching anchor price:', err);
			}
		};
		fetchAnchorPrice();
	}, [token]);

	const anchorsTotalPrice = useMemo(() => {
		const qty = parseInt(formAdditionalOptionsData?.anchors, 10) || 0;
		return anchorUnitPrice * qty;
	}, [anchorUnitPrice, formAdditionalOptionsData?.anchors]);

	// Additional Bracing (cat 55) — price lookup by height × quantity
	const [bracingUnitPrice, setBracingUnitPrice] = useState(0);
	useEffect(() => {
		if (!cSHeight) return;
		const fetchBracingPrice = async () => {
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'ASC', categoryId: 55, height: Number(cSHeight) },
					headers: { Authorization: `Bearer ${token}` },
				});
				setBracingUnitPrice(res.data.items?.[0]?.defaultPrice || 0);
			} catch (err) {
				console.error('Error fetching bracing price:', err);
			}
		};
		fetchBracingPrice();
	}, [token, cSHeight]);

	const bracingTotalPrice = useMemo(() => {
		const qty = parseInt(formAdditionalOptionsData?.aditionalBracing, 10) || 0;
		return bracingUnitPrice * qty;
	}, [bracingUnitPrice, formAdditionalOptionsData?.aditionalBracing]);

	// Diagonal Bracing (cat 5) — auto-calculated based on height, length, sides/ends
	const autoBracingCount = useMemo(() => {
		const h = parseFloat(cSHeight) || 0;
		const l = parseFloat(cSLength) || 0;
		if (h <= 15) return 0;

		const cs = sideEndData?.configuration?.centerSection;
		const isClosed = (val) => val === true || val === 'closed';
		const isFullyClosed =
			isClosed(cs?.leftSidewall) &&
			isClosed(cs?.rightSidewall) &&
			isClosed(cs?.frontGableEnd) &&
			isClosed(cs?.backGableEnd);

		let base;
		if (l <= 50) {
			base = 4;
		} else {
			base = isFullyClosed ? 8 : 6;
		}

		const overage = l > 50 ? Math.floor((l - 50) / 10) * 2 : 0;
		return base + overage;
	}, [cSHeight, cSLength, sideEndData]);

	const [autoBracingUnitPrice, setAutoBracingUnitPrice] = useState(0);
	useEffect(() => {
		if (!cSHeight) return;
		const fetchAutoBracingPrice = async () => {
			try {
				const res = await api.get('/Materials', {
					params: { pageSize: 1, page: 1, sortOrder: 'ASC', categoryId: 5, height: Number(cSHeight) },
					headers: { Authorization: `Bearer ${token}` },
				});
				setAutoBracingUnitPrice(res.data.items?.[0]?.defaultPrice || 0);
			} catch (err) {
				console.error('Error fetching auto bracing price:', err);
			}
		};
		fetchAutoBracingPrice();
	}, [token, cSHeight]);

	const autoBracingTotalPrice = useMemo(() => {
		return autoBracingUnitPrice * autoBracingCount;
	}, [autoBracingUnitPrice, autoBracingCount]);

	// Panel upgrade / colored screws — applied as % of (all gauge base + sidewalls)
	const panelUpgradePrice = useMemo(() => {
		const upgrade = formAdditionalOptionsData?.panelUpgrade;
		if (!upgrade || upgrade === 'standardPanel29Gauge') return 0;
		const structureBase =
			totalCenterGauge + leftLeanPrice + rightLeanPrice + frontLeanPrice + backLeanPrice;
		const base = structureBase + totalSidewallCost;
		if (upgrade === '26GaugePanelUpgrade') return base * 0.15;
		if (upgrade === 'coloredScrews') return base * 0.02;
		return 0;
	}, [
		formAdditionalOptionsData?.panelUpgrade,
		totalCenterGauge, leftLeanPrice, rightLeanPrice, frontLeanPrice, backLeanPrice,
		totalSidewallCost,
	]);

	const windosandDoorPrice = useMemo(() => {
		return (
			doorWindowSelections?.reduce((acc, selection) => {
				const price =
					typeof selection.defaultPrice === 'object'
						? selection.defaultPrice.value
						: selection.defaultPrice;
				return acc + price;
			}, 0) || 0
		);
	}, [doorWindowSelections]);

	// Subtotal
	const subTotal = useMemo(() => {
		return calculateSubTotal({
			totalCenterGauge,
			leftLeanPrice,
			rightLeanPrice,
			backLeanPrice,
			frontLeanPrice,
			totalCenterLegPrice,
			totalLeftLegPrice,
			totalRightLegPrice,
			totalBackLegPrice,
			totalFrontLegPrice,
			totalRoofStylePrice,
			totalSidewallCost,
			windosandDoorPrice,
			totalRoofPitchPrice,
			frontRoofPitchPrice,
			leftSideRoofPitchPrice,
			rightSideRoofPitchPrice,
			backRoofPitchPrice,
			totalDiscount,
			totalInsulationPrice,
			panelUpgradePrice,
			anchorsTotalPrice,
			bracingTotalPrice,
			autoBracingTotalPrice,
			trussesTotalPrice,
			texasCutTotal,
			storagePanelsTotal,
			foamClosureTotal,
		});
	}, [
		totalCenterGauge,
		leftLeanPrice,
		rightLeanPrice,
		backLeanPrice,
		frontLeanPrice,
		totalCenterLegPrice,
		totalLeftLegPrice,
		totalRightLegPrice,
		totalBackLegPrice,
		totalFrontLegPrice,
		totalRoofStylePrice,
		totalSidewallCost,
		windosandDoorPrice,
		totalRoofPitchPrice,
		frontRoofPitchPrice,
		leftSideRoofPitchPrice,
		rightSideRoofPitchPrice,
		backRoofPitchPrice,
		totalDiscount,
		totalInsulationPrice,
		panelUpgradePrice,
		anchorsTotalPrice,
		bracingTotalPrice,
		autoBracingTotalPrice,
		trussesTotalPrice,
		texasCutTotal,
		storagePanelsTotal,
		foamClosureTotal,
	]);

	// Total estimate for the building (often used for metadata.mbPrice)
	const totalEstimate = useMemo(() => {
		return (
			totalCenterGauge +
			leftLeanPrice +
			rightLeanPrice +
			backLeanPrice +
			frontLeanPrice +
			totalCenterLegPrice +
			totalLeftLegPrice +
			totalRightLegPrice +
			totalBackLegPrice +
			totalFrontLegPrice +
			windosandDoorPrice +
			totalSidewallCost +
			totalRoofStylePrice +
			totalInsulationPrice +
			panelUpgradePrice +
			anchorsTotalPrice +
			bracingTotalPrice +
			autoBracingTotalPrice +
			trussesTotalPrice +
			texasCutTotal +
			storagePanelsTotal +
			foamClosureTotal -
			totalDiscount
		);
	}, [
		totalCenterGauge,
		leftLeanPrice,
		rightLeanPrice,
		backLeanPrice,
		frontLeanPrice,
		totalCenterLegPrice,
		totalLeftLegPrice,
		totalRightLegPrice,
		totalBackLegPrice,
		totalFrontLegPrice,
		windosandDoorPrice,
		totalSidewallCost,
		totalRoofStylePrice,
		totalInsulationPrice,
		panelUpgradePrice,
		anchorsTotalPrice,
		bracingTotalPrice,
		autoBracingTotalPrice,
		trussesTotalPrice,
		texasCutTotal,
		storagePanelsTotal,
		foamClosureTotal,
		totalDiscount,
	]);

	// Helper to get window/door items for a specific section
	const getSectionItems = (sectionName) => {
		if (!doorWindowSelections) return [];
		return doorWindowSelections
			.filter((item) => item.metadata?.section === sectionName)
			.map((item) => ({
				id:
					item.category === 'door' || item.category === 'window'
						? item.category
						: 'item',
				name: item.description || item.category,
				price:
					typeof item.defaultPrice === 'object'
						? item.defaultPrice.value
						: item.defaultPrice,
				quantity: item.quantity || 1,
			}));
	};

	// ---------------------------------------------------------------------------
	// Construct Detailed Price Arrays (for 3D Portal Payload)
	// ---------------------------------------------------------------------------

	const centerPriceArray = useMemo(() => {
		const items = [
			{ id: 'basePrice', name: 'base', price: totalCenterGauge },
			{ id: 'legPrice', name: 'legHeight', price: totalCenterLegPrice },
			// Roof price is currently aggregated, simplified assignment roughly or 0 if part of base in some styles
			// In legacy logic roof price might be separated. For now we use the total calculation if available
			{
				id: 'roofPrice',
				name: 'roof',
				price: totalRoofStylePrice > 0 ? totalRoofStylePrice : 0,
			},
			{ id: 'csPrice', name: 'storage', price: centerStoragePrice },
		];

		// Add Walls if present in sidewallCosts
		if (sidewallCosts?.centerSectionSide) {
			const walls = sidewallCosts.centerSectionSide;
			if (walls.frontGableEnd)
				items.push({
					id: 'frontWallPrice',
					name: 'frontWall',
					price: walls.frontGableEnd,
				});
			if (walls.backGableEnd)
				items.push({
					id: 'backWallPrice',
					name: 'backWall',
					price: walls.backGableEnd,
				});
			if (walls.leftSidewall)
				items.push({
					id: 'leftWallPrice',
					name: 'leftWall',
					price: walls.leftSidewall,
				});
			if (walls.rightSidewall)
				items.push({
					id: 'rightWallPrice',
					name: 'rightWall',
					price: walls.rightSidewall,
				});
		}

		return [...items, ...getSectionItems('centerSection')].filter(
			(i) => i.price > 0
		);
	}, [
		totalCenterGauge,
		totalCenterLegPrice,
		totalRoofStylePrice,
		centerStoragePrice,
		sidewallCosts,
		doorWindowSelections,
	]);

	const leftPriceArray = useMemo(() => {
		const items = [
			{ id: 'basePrice', name: 'base', price: leftLeanPrice },
			{ id: 'legPrice', name: 'legHeight', price: totalLeftLegPrice },
			{ id: 'llsPrice', name: 'storage', price: leftleanstoragePrice },
		];
		if (sidewallCosts?.leftLeanToSide) {
			const walls = sidewallCosts.leftLeanToSide;
			if (walls.frontGableEnd)
				items.push({
					id: 'frontWallPrice',
					name: 'frontWall',
					price: walls.frontGableEnd,
				});
			if (walls.backGableEnd)
				items.push({
					id: 'backWallPrice',
					name: 'backWall',
					price: walls.backGableEnd,
				});
			if (walls.leftSidewall)
				items.push({
					id: 'leftWallPrice',
					name: 'leftWall',
					price: walls.leftSidewall,
				});
			if (walls.rightSidewall)
				items.push({
					id: 'rightWallPrice',
					name: 'rightWall',
					price: walls.rightSidewall,
				});
		}
		return [...items, ...getSectionItems('leftLeanTo')].filter(
			(i) => i.price > 0
		);
	}, [
		leftLeanPrice,
		totalLeftLegPrice,
		leftleanstoragePrice,
		sidewallCosts,
		doorWindowSelections,
	]);

	const rightPriceArray = useMemo(() => {
		const items = [
			{ id: 'basePrice', name: 'base', price: rightLeanPrice },
			{ id: 'legPrice', name: 'legHeight', price: totalRightLegPrice },
			{ id: 'rlsPrice', name: 'storage', price: rightStoragePrice },
		];
		if (sidewallCosts?.rightLeanToSide) {
			const walls = sidewallCosts.rightLeanToSide;
			if (walls.frontGableEnd)
				items.push({
					id: 'frontWallPrice',
					name: 'frontWall',
					price: walls.frontGableEnd,
				});
			if (walls.backGableEnd)
				items.push({
					id: 'backWallPrice',
					name: 'backWall',
					price: walls.backGableEnd,
				});
			if (walls.leftSidewall)
				items.push({
					id: 'leftWallPrice',
					name: 'leftWall',
					price: walls.leftSidewall,
				});
			if (walls.rightSidewall)
				items.push({
					id: 'rightWallPrice',
					name: 'rightWall',
					price: walls.rightSidewall,
				});
		}
		return [...items, ...getSectionItems('rightLeanTo')].filter(
			(i) => i.price > 0
		);
	}, [
		rightLeanPrice,
		totalRightLegPrice,
		rightStoragePrice,
		sidewallCosts,
		doorWindowSelections,
	]);

	const frontPriceArray = useMemo(() => {
		const items = [
			{ id: 'basePrice', name: 'base', price: frontLeanPrice },
			{ id: 'legPrice', name: 'legHeight', price: totalFrontLegPrice },
			{ id: 'flsPrice', name: 'storage', price: frontStoragePrice },
		];
		if (sidewallCosts?.frontLeanToSide) {
			const walls = sidewallCosts.frontLeanToSide;
			if (walls.frontGableEnd)
				items.push({
					id: 'frontWallPrice',
					name: 'frontWall',
					price: walls.frontGableEnd,
				});
			if (walls.backGableEnd)
				items.push({
					id: 'backWallPrice',
					name: 'backWall',
					price: walls.backGableEnd,
				});
			if (walls.leftSidewall)
				items.push({
					id: 'leftWallPrice',
					name: 'leftWall',
					price: walls.leftSidewall,
				});
			if (walls.rightSidewall)
				items.push({
					id: 'rightWallPrice',
					name: 'rightWall',
					price: walls.rightSidewall,
				});
		}
		return [...items, ...getSectionItems('frontLeanTo')].filter(
			(i) => i.price > 0
		);
	}, [
		frontLeanPrice,
		totalFrontLegPrice,
		frontStoragePrice,
		sidewallCosts,
		doorWindowSelections,
	]);

	const backPriceArray = useMemo(() => {
		const items = [
			{ id: 'basePrice', name: 'base', price: backLeanPrice },
			{ id: 'legPrice', name: 'legHeight', price: totalBackLegPrice },
			{ id: 'blsPrice', name: 'storage', price: backStoragePrice },
		];
		if (sidewallCosts?.backLeanToSide) {
			const walls = sidewallCosts.backLeanToSide;
			if (walls.frontGableEnd)
				items.push({
					id: 'frontWallPrice',
					name: 'frontWall',
					price: walls.frontGableEnd,
				});
			if (walls.backGableEnd)
				items.push({
					id: 'backWallPrice',
					name: 'backWall',
					price: walls.backGableEnd,
				});
			if (walls.leftSidewall)
				items.push({
					id: 'leftWallPrice',
					name: 'leftWall',
					price: walls.leftSidewall,
				});
			if (walls.rightSidewall)
				items.push({
					id: 'rightWallPrice',
					name: 'rightWall',
					price: walls.rightSidewall,
				});
		}
		return [...items, ...getSectionItems('backLeanTo')].filter(
			(i) => i.price > 0
		);
	}, [
		backLeanPrice,
		totalBackLegPrice,
		backStoragePrice,
		sidewallCosts,
		doorWindowSelections,
	]);

	return {
		prices: {
			totalCenterGauge,
			totalCenterLegPrice,
			leftLeanPrice,
			totalLeftLegPrice,
			rightLeanPrice,
			totalRightLegPrice,
			frontLeanPrice,
			totalFrontLegPrice,
			backLeanPrice,
			totalBackLegPrice,
			totalRoofStylePrice,
			totalRoofPitchPrice,
			leftSideRoofPitchPrice,
			rightSideRoofPitchPrice,
			frontRoofPitchPrice,
			backRoofPitchPrice,
			subTotal,
			totalEstimate,
			totalSidewallCost,
			windosandDoorPrice,
			centerStoragePrice,
			leftleanstoragePrice,
			rightStoragePrice,
			frontStoragePrice,
			backStoragePrice,
			totalInsulationPrice,
			insulationPriceBreakdown,
			panelUpgradePrice,
			anchorsTotalPrice,
			bracingTotalPrice,
			autoBracingTotalPrice,
			autoBracingCount,
			trussesTotalPrice,
			texasCutTotal,
			storagePanelsTotal,
			storagePanelsBreakdown,
			foamClosureTotal,
			// Export the structured arrays
			centerPriceArray,
			leftPriceArray,
			rightPriceArray,
			frontPriceArray,
			backPriceArray,
		},
		getDynamicPrice,
	};
};
